/* eslint-disable */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { makeStyles } from '@material-ui/core';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import RoundedButton from 'components/Shared/RoundedButton';
import UnsavedChangesGuard from 'components/Shared/UnsavedChangesGuard';
import OrderForm from './widgets/OrderForm';
import BackButton from 'components/Shared/BackButton';
import { checkPermission } from 'helpers/checkPermissions';
import permissions from 'config/permissions';
import { ORDER_STATUS_OPTIONS } from 'components/Orders/constants';
import { useDispatch, useSelector } from 'react-redux';
import EmptyPageLoader from '../../Shared/EmptyPageLoader';
import MetadataErrorWidget from './widgets/MetadataErrorWidget';
import {
  fetchCreateOrderMetadata,
  fetchOrder,
  updateOrder,
  resetUpdateStatus,
  resetOrderState,
  REQUEST_STATUS,
  navigateToRoute,
  SNACKBAR_VARIANTS,
} from '../../../redux_store';
import { ALL_ROUTES } from '../../../config/routes';
import useFormikSuccessReset from '../../../hooks/useFormikSuccessReset';


const useStyles = makeStyles((theme) => ({
  contentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
  },
  formCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    marginBottom: theme.spacing(10),
    margin: theme.spacing(3, 0),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
      borderRadius: 12,
    },
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
  },
  formContent: {
    width: '100%',
  },
  actionsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    padding: theme.spacing(3, 6, 4),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 4, 3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2, 3),
      gap: theme.spacing(1),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100,
      padding: theme.spacing(1, 2),
      fontSize: 13,
    },
  },
  backButton: {
    marginRight: 'auto',
  },
}));

const OrderEdit = () => {
  const classes = useStyles();
  const { accountUuid, orderUuid, patientUuid } = useParams();
  const dispatch = useDispatch();
  const {
    user,
    metadata: {
      currentAccount,
      currentAccountPermissions,
      childAccountPermissions,
    },
    isDistributionSponsor,
  } = useSelector((state) => state.user);
  const {
    en: {
      titles,
      errors,
      notifications,
      btn,
      unsavedChanges,
    },
  } = useSelector((state) => state.i18n);
  const {
    status,
    loadedOrderData: order,
    metadata,
    updateStatus,
    backendErrors,
    metadataStatus,
  } = useSelector((state) => state.order);

  const metadataError = metadataStatus === REQUEST_STATUS.failed;
  const orderError = status === REQUEST_STATUS.failed;
  const loadingOrder = status === REQUEST_STATUS.loading;
  const loadingMetadata = metadataStatus === REQUEST_STATUS.loading;
  const updating = updateStatus === REQUEST_STATUS.loading;
  const isSuccess = updateStatus === REQUEST_STATUS.succeeded;
  const isFailed = updateStatus === REQUEST_STATUS.failed;

  // Use hook for success reset
  const formikRef = useFormikSuccessReset(isSuccess, () => {
    dispatch(resetUpdateStatus());
    dispatch(navigateToRoute({
      route: {
        path: ALL_ROUTES.order.list.path,
        title: ALL_ROUTES.order.list.additional.title,
        params: {
          currentAccountUuid: currentAccount.uuid,
        },
        clearBreadcrumbs: true,
      },
      addDelay: true,
    }));
  });

  // Load metadata and order data on component mount
  useEffect(() => {
    const accountId = accountUuid || currentAccount.uuid;
    dispatch(fetchCreateOrderMetadata({ accountUuid: accountId, patientUuid }));
    dispatch(fetchOrder({ reorderUuid: orderUuid, accountUuid: accountId }));

    // Cleanup function to reset order state when component unmounts
    return () => {
      dispatch(resetOrderState());
    };
  }, [accountUuid, currentAccount.uuid, patientUuid, orderUuid, dispatch]);

  const onNotPermission = () => {
    window.history.back();
    dispatch(
      enqueueSnackbar({
        message: notifications.notPermissionForEditingOrder,
        options: {
          variant: SNACKBAR_VARIANTS.warning,
        },
      }),
    );
  };

  // Handle update status changes
  useEffect(() => {
    if (updateStatus === REQUEST_STATUS.failed) {
      setTimeout(() => {
        dispatch(resetUpdateStatus());
      }, 2000);
    }
  }, [updateStatus, dispatch]);

  useEffect(() => {
    const hasPermission = checkPermission(
      !accountUuid
        ? currentAccountPermissions
        : childAccountPermissions,
      permissions.ORDER_PATCH,
    );
    if (!hasPermission) {
      onNotPermission() ;
    }
  }, []);

  useEffect(() => {
    if (metadataStatus === 401) {
      onNotPermission();
    }
  }, [metadataStatus]);

  const editOrder = async (values) => {
    dispatch(updateOrder({
      accountUuid: accountUuid || currentAccount.uuid,
      orderUuid,
      formValues: values,
      originalOrder: order,
      userUuid: user.uuid,
      metadata,
    }));
  };

  if (metadataError || orderError) {
    return <MetadataErrorWidget />;
  }

  if (loadingMetadata || loadingOrder || !order?.data || !metadata) {
    return <EmptyPageLoader />;
  }

  const isEditable = isDistributionSponsor
    || ORDER_STATUS_OPTIONS
    .find((orderStatus) => orderStatus.value === order.data.status)
    && ORDER_STATUS_OPTIONS
    .find((orderStatus) => orderStatus.value === order.data.status).allowEditOrder;

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        ...order.data,
        slitLowDose: !!order.data.slitLowDoseQuantity,
        slitMaintenance: !!order.data.slitMaintenanceQuantity,
      }}
      validateOnBlur={false}
      enableReinitialize
      onSubmit={editOrder}
    >
      {({ isValid, isSubmitting, dirty, handleSubmit, values }) => (
        <UnsavedChangesGuard
          hasUnsavedChanges={dirty}
          title={unsavedChanges.title}
          message={unsavedChanges.orderEdit}
          discardButtonText={unsavedChanges.leaveAnyway}
          keepEditingButtonText={unsavedChanges.keepEditing}
        >
          <ContentWrapper
            titleText={titles.editOrder}
            containerStyle={{ overflow: 'visible' }}
          >
            <div className={classes.contentContainer}>
            <div className={classes.formCard}>
              <div className={classes.scrollableContent}>
                <div className={classes.formContent}>
                  <OrderForm
                    values={values}
                    metadata={metadata}
                    backendErrors={backendErrors}
                    isEditable={isEditable}
                  />
                </div>
              </div>
            </div>
            <div className={classes.actionsContainer}>
              <BackButton className={classes.backButton} />
              <RoundedButton
                type='submit'
                variant='contained'
                color='primary'
                disabled={!isValid || isSubmitting || !dirty || !isEditable}
                onClick={handleSubmit}
                isLoading={updating}
                isSuccess={isSuccess}
                isFailed={isFailed}
                className={classes.actionButton}
              >
                {btn.save}
              </RoundedButton>
            </div>
          </div>
        </ContentWrapper>
        </UnsavedChangesGuard>
      )}
    </Formik>
  );
};

export default OrderEdit;
